import React from 'react';
import { cn } from '../../lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-museum",
        "bg-museum-100 dark:bg-museum-800",
        className
      )}
      {...props}
    />
  );
};

// Mandala Card Skeleton
export const MandalaCardSkeleton: React.FC = () => {
  return (
    <div className={cn(
      "relative group",
      "bg-white dark:bg-museum-950",
      "border border-museum-200 dark:border-museum-800",
      "rounded-museum",
      "p-8"
    )}>
      {/* Large Number */}
      <Skeleton className="w-20 h-24 mb-6" />

      {/* Title */}
      <Skeleton className="h-7 w-32 mb-2" />

      {/* Subtitle */}
      <Skeleton className="h-4 w-20" />
    </div>
  );
};

// Hymn Card Skeleton
export const HymnCardSkeleton: React.FC = () => {
  return (
    <div className={cn(
      "bg-white dark:bg-museum-950",
      "border border-museum-200 dark:border-museum-800",
      "rounded-museum p-6"
    )}>
      {/* Number and icon row */}
      <div className="flex items-start justify-between mb-4">
        <Skeleton className="w-12 h-10" />
        <Skeleton className="w-4 h-4 rounded-full" />
      </div>

      {/* Title */}
      <Skeleton className="h-5 w-24 mb-1" />

      {/* Description */}
      <Skeleton className="h-4 w-32 mb-3" />

      {/* Badge */}
      <Skeleton className="h-6 w-16 rounded-museum" />
    </div>
  );
};

// Verse Content Skeleton
export const VerseContentSkeleton: React.FC = () => {
  return (
    <div className={cn(
      "max-w-4xl mx-auto",
      "bg-white dark:bg-museum-950",
      "border border-museum-200 dark:border-museum-800",
      "rounded-museum"
    )}>
      {/* Header */}
      <div className="text-center p-8 border-b border-museum-100 dark:border-museum-800">
        {/* Badges */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <Skeleton className="h-6 w-24 rounded-museum" />
          <Skeleton className="h-6 w-20 rounded-museum" />
          <Skeleton className="h-6 w-16 rounded-museum" />
        </div>

        {/* Title */}
        <Skeleton className="h-8 w-48 mx-auto mb-2" />

        {/* Rishi */}
        <Skeleton className="h-4 w-32 mx-auto" />
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Verse Navigator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="w-8 h-8 rounded-museum" />
          ))}
        </div>

        {/* Tabs */}
        <Skeleton className="h-10 w-full mb-8 rounded-museum" />

        {/* Content Box */}
        <div className={cn(
          "p-8",
          "bg-museum-50 dark:bg-museum-900/50",
          "border border-museum-100 dark:border-museum-800",
          "rounded-museum"
        )}>
          <Skeleton className="h-8 w-3/4 mx-auto mb-4" />
          <Skeleton className="h-8 w-2/3 mx-auto mb-4" />
          <Skeleton className="h-8 w-1/2 mx-auto" />
        </div>
      </div>
    </div>
  );
};

// Grid of Mandala Skeletons
export const MandalaGridSkeleton: React.FC<{ count?: number }> = ({ count = 10 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-fade-in-up"
          style={{ animationDelay: `${i * 50}ms` }}
        >
          <MandalaCardSkeleton />
        </div>
      ))}
    </div>
  );
};

// Grid of Hymn Skeletons
export const HymnGridSkeleton: React.FC<{ count?: number }> = ({ count = 12 }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-fade-in-up"
          style={{ animationDelay: `${i * 30}ms` }}
        >
          <HymnCardSkeleton />
        </div>
      ))}
    </div>
  );
};

export default Skeleton;
